import { Component, Input, OnDestroy } from '@angular/core';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { RightPanelService } from '../../core/services/right-panel.service';
import { TicketService } from '../../core/services/ticket/ticket.service';
import { ConfirmationModalComponent } from '../../shared/modal-windows/confirmation-modal/confirmation-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificationsService } from 'angular2-notifications';
import { HelperService } from 'src/sb-tools/typescript/services/helper.service';
import { SmilesService } from 'src/sb-tools/typescript/services/smiles.service';
import Quill from 'quill';
import { DetailedTicketDTO } from '../../core/models/dto/detailed-ticket.dto';
import { RedactAttachmentsResponseModel } from '../../core/models/redact-attachments-response.model';
import { TicketRedactionModel } from '../../core/models/ticket-redaction.model';
import { QuillEditorComponent } from 'ngx-quill';
import { FormsModule } from '@angular/forms';
import { LoaderComponent } from '../../../sb-tools/typescript/components/loader/loader.component';
import { FilePreviewComponent } from '../../../sb-modules/Frontend-Document-Viewer-Module/components/file-preview/file-preview.component';
import { SafeHtmlPipe } from '../../../sb-tools/typescript/pipes/safe-html.pipe';

@Component({
    selector: 'app-redact-ticket',
    templateUrl: './redact-ticket.component.html',
    styleUrls: ['./redact-ticket.component.scss'],
    imports: [TranslateModule, QuillEditorComponent, FormsModule, LoaderComponent, FilePreviewComponent, SafeHtmlPipe],
})
export class RedactTicketComponent implements OnDestroy {
    @Input() ticket: DetailedTicketDTO;

    attachmentsData: RedactAttachmentsResponseModel = {
        attachments: [],
        inlineImages: [],
    };
    isEditable: boolean;
    isLoading: boolean;
    isSaving: boolean;
    isAttachmentsLoaded: boolean;
    isAllAttachmentsSelected: boolean;
    isAllInlineAttachmentsSelected: boolean;
    isSomeAttachmentsSelected: boolean;
    hasAttachments: boolean;
    hasInlineImages: boolean;
    quillConfig = {
        toolbar: false,
    };
    quillEditor: Quill;
    redactModel: TicketRedactionModel;
    textToRedact = '';
    textToRedactModel: string;

    private unsubscriber = new Subject<void>();

    constructor(
        private rightPanelService: RightPanelService,
        private ticketService: TicketService,
        private smilesService: SmilesService,
        private bsModalService: BsModalService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
    ) {}

    closePanel(): void {
        this.rightPanelService.close();
    }

    confirmRedact() {
        const initialState = {
            content:
                '<p class="mb-2">' +
                this.translateService.instant(
                    'The selected text, images and attachments will be deleted. This action cannot be undone.',
                ) +
                '</p>' +
                '<p class="mb-4">' +
                this.translateService.instant('Do you still want to complete the redaction?') +
                '</p>',
            confirmText: this.translateService.instant('Yes, complete the redaction'),
            declineText: this.translateService.instant('No, cancel'),
            confirmCallback: () => {
                return this.save();
            },
        };

        this.bsModalService.show(ConfirmationModalComponent, {
            initialState,
        });
    }

    loadAttachments() {
        this.isLoading = true;
        this.ticketService
            .getRedactAttachments(this.ticket.id)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((data) => {
                this.attachmentsData = data;

                this.hasAttachments = !!(this.attachmentsData.attachments && this.attachmentsData.attachments.length);
                this.hasInlineImages = !!(
                    this.attachmentsData.inlineImages && this.attachmentsData.inlineImages.length
                );

                this.isAttachmentsLoaded = true;
            });
    }

    onTextChanged(data) {
        this.textToRedact = HelperService.strip(this.smilesService.replaceImagesToCodes(data.html), false);
    }

    onEditorCreated(quill) {
        this.quillEditor = quill;
        quill.focus();
    }

    preventSelectItem(event) {
        event.preventDefault();
    }

    selectAttachment(isInlineImage) {
        if (isInlineImage) {
            this.isAllInlineAttachmentsSelected = this.attachmentsData.inlineImages.every((val) => {
                return val.isMarked;
            });
        } else {
            this.isAllAttachmentsSelected = this.attachmentsData.attachments.every((val) => {
                return val.isMarked;
            });
        }

        this.checkIfSomeAttachmentsSelected();
    }

    toggleSelectAll(val, attachmentType: 'inlineImages' | 'attachments') {
        this.attachmentsData[attachmentType].forEach((attachment) => {
            attachment.isMarked = val;
        });

        this.checkIfSomeAttachmentsSelected();
    }

    private checkIfSomeAttachmentsSelected() {
        this.isSomeAttachmentsSelected =
            this.attachmentsData.inlineImages.some((image) => {
                return image.isMarked;
            }) ||
            this.attachmentsData.attachments.some((attachment) => {
                return attachment.isMarked;
            });
    }

    private save(): void {
        this.isSaving = true;
        this.ticketService.isRedacting = true;
        this.redactModel = new TicketRedactionModel(this.textToRedact, this.attachmentsData);

        this.ticketService
            .redactTicket(this.ticket.id, this.redactModel)
            .pipe(
                finalize(() => {
                    this.isSaving = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe(
                (data) => {
                    this.ticketService.emitTicketUpdated(data);
                    this.notificationsService.success(
                        this.translateService.instant('Success!'),
                        this.translateService.instant('Ticket was successfully redacted.'),
                    );
                    this.resetForm();
                },
                () => {
                    this.ticketService.isRedacting = false;
                },
            );
    }

    private resetForm() {
        this.textToRedactModel = '';
        this.isAttachmentsLoaded = false;
        this.attachmentsData = {
            attachments: [],
            inlineImages: [],
        };
        this.isSomeAttachmentsSelected = false;
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
